import { Component, computed, OnDestroy, Signal, inject, TemplateRef, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { PlaygroundService, PlaygroundRunInfo, EndpointCatalogService, NotificationService, SessionService, CikisiResourcesService } from '../../../core/services';
import { apiErrorMessage } from '../../../core/utils/api-error-message';
import { ModalService } from '../../../shared/modal';
import { openCikisiFilterSheet } from '../cikisi-filter-sheet/cikisi-filter-sheet';
import { isPhoneViewport } from '../../../core/utils/phone';
import { openHelpSheet } from '../help-sheet/help-sheet';
import { openDateSheet } from '../../../shared/date-sheet/date-sheet';
import { EndpointDefinition, getEndpointDefinition } from '../../../core/models';

import { RagResult } from '../models/rag-result.model';
import { buildCodeSnippets, CodeLanguage } from '../utils/code-snippet';
import { estimateEndpointPrice, PriceEstimate } from '../utils/endpoint-pricing';
import { PlaygroundQueryPage } from '../utils/playground-query-page';
import { buildCikisiFields } from '../utils/cikisi-fields';
import { GeolocatedArea } from '../models/geolocated-area.model';
import { PromptOptimizer } from '../prompt-optimizer/prompt-optimizer';
import {
  MilaMultiSelectFetcher,
  MilaMultiSelectOption,
} from '../../../shared/mila-multi-select/mila-multi-select-option.model';
import { PlaygroundKeyService } from '../utils/playground-key.service';

export interface FilterRowMeta {
  id: string;
  label: string;
  placeholder: string;
  pricingParam: string;
  payloadField: string;
  /** 'text' = free-typed chips (Knowledge Base one per account, nothing
   *  to look up); 'flat' = backed by MilaMultiSelect via
   *  CikisiResourcesService all six lookup resources are flat, none
   *  hierarchical. */
  kind: 'text' | 'flat';
}

export interface CalState {
  open: boolean;
  year: number;
  month: number;
  selected: Date | null;
}

@Component({
  selector: 'app-rag',
  standalone: false,
  templateUrl: './rag.html',
  styleUrl: './rag.scss',
})
export class Rag implements OnDestroy, PlaygroundQueryPage {
  constructor(
    private fb: FormBuilder,
    private playgroundService: PlaygroundService,
    private catalogService: EndpointCatalogService,
    private notificationService: NotificationService,
    private lookupService: CikisiResourcesService,
    private sessionService: SessionService,
    private modalService: ModalService,
  ) {
    // authGuard already resolved /me before this page can render, so the
    // real organization is available synchronously here see Settings/Home
    // for the same convention. Cikisi-only filtering only makes sense (and
    // is only sent) for orgs actually flagged as Cikisi users.
    this.isCikisiUser = this.sessionService.organization?.isCikisiUser ?? false;

    this.form = this.buildForm();

    this.endpointDef = computed(() => getEndpointDefinition(this.catalogService.catalog(), 'rag'));


    this.flatFetchers = {
      'p-tags': this.lookupService.searchTags,
      'p-sc': this.lookupService.searchSourceCategories,
      'p-ss': this.lookupService.searchSavedSearches,
      'p-si': this.lookupService.searchSourceIds,
      'p-kt': this.lookupService.searchKnowledgeTopics,
      'p-th': this.lookupService.searchThesaurusClasses,
    };
    this.languageFetcher = this.lookupService.searchLanguages;
  }

  readonly flatFetchers: Record<string, MilaMultiSelectFetcher>;
  /** Backs the "Language search" field the static ISO 639-1 list, see CikisiResourcesService. */
  readonly languageFetcher: MilaMultiSelectFetcher;

  /** Gates the whole "Search only Cikisi content" section see rag.html. */
  readonly isCikisiUser: boolean;

  private readonly _destroy$ = new Subject<void>();

  readonly endpointDef: Signal<EndpointDefinition | undefined>;

  isLoading = false;
  status: 'idle' | 'ok' | 'error' = 'idle';
  response: RagResult | null = null;
  latencyMs: number | null = null;
  credits: number | null = null;
  runInfo: PlaygroundRunInfo | null = null;
  errorMessage: string | null = null;
  loadingFields: ReadonlySet<string> = new Set();
  readonly resultView = 'rag' as const;
  readonly emptyHint = 'The result will include passages and their sources, based on your configuration.';

  activeLanguage: CodeLanguage = 'ruby';
  copied = false;

  // ── Collapsible sections Response starts collapsed, it's set once and
  // rarely revisited; the other two are what changes query to query. ──
  sectionOpen: Record<'question' | 'request' | 'response', boolean> = {
    question: true,
    request: true,
    response: false,
  };

  toggleSection(key: keyof Rag['sectionOpen']): void {
    this.sectionOpen[key] = !this.sectionOpen[key];
  }

  /** The key runs bill to: picked once in the run bar (playground.html). */
  private readonly apiKey = inject(PlaygroundKeyService);

  // Real backend param names per row come from the /rag `parameters_echo` sample.
  // pricingParam is the confirmed GET /endpoints name for this row (see
  // FilterRowMeta) it's used for pricing only and left independent of
  // payloadField, which still has its own known wire-format issues.
  readonly FILTER_ROW_META: FilterRowMeta[] = [
    { id: 'p-kt',   label: 'Knowledge Topics',    placeholder: 'Search knowledge topics…', pricingParam: 'knowledge_topics',   payloadField: 'knowledge_topics',   kind: 'flat' },
    { id: 'p-tags', label: 'Tags',                placeholder: 'Search tags…',             pricingParam: 'tags',               payloadField: 'tags_ids',               kind: 'flat' },
    { id: 'p-sc',   label: 'Source categories',   placeholder: 'Search categories…',       pricingParam: 'source_categories',  payloadField: 'source_categories_ids',  kind: 'flat' },
    { id: 'p-th',   label: 'Thesaurus / classes', placeholder: 'Search classes…',          pricingParam: 'thesaurus_classes',  payloadField: 'thesaurus_classes_ids',  kind: 'flat' },
    { id: 'p-ss',   label: 'Saved searches',      placeholder: 'Search saved searches…',   pricingParam: 'saved_searches',     payloadField: 'saved_searches_ids',     kind: 'flat' },
    { id: 'p-si',   label: 'Source IDs',          placeholder: 'Search source IDs…',       pricingParam: 'source_ids',         payloadField: 'source_ids',         kind: 'flat' },
  ];

  readonly MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  readonly DAY_NAMES = ['Su','Mo','Tu','We','Th','Fr','Sa'];

  form: FormGroup;

  private buildForm(): FormGroup {
    return this.fb.group({
      question: ['', Validators.required],
      cikisiOnly: [this.isCikisiUser],
      // Its own field, a sibling of cikisiOnly rather than folded into
      // the Knowledge Topics row see buildCikisiFields()/
      // toggleKnowledgeBase().
      knowledgeBase: [false],
      searchImageDescriptions: [false],
      relevanceCriteriaCount: [5],
      useSourcePopularity: [false],
      comprehensiveness: [120],
      includeRelevanceScore: [false],
      includeOriginalLanguageSnippet: [false],
      includeArticleTitle: [false],
      includeImageUrl: [false],
      includeDomainName: [false],
      includeFavicon: [false],
      filterRows: this.fb.array(this.FILTER_ROW_META.map(meta => this.buildFilterRowGroup(meta))),
      includeChips: this.fb.array<string>([]),
      excludeChips: this.fb.array<string>([]),
      geolocatedAreas: this.fb.array<GeolocatedArea>([]),
      languageSearch: this.fb.control<MilaMultiSelectOption[]>([]),
    });
  }

  private buildFilterRowGroup(meta: FilterRowMeta): FormGroup {
    return this.fb.group({
      expanded: [false],
      chips: this.fb.control<MilaMultiSelectOption[]>([]),
    });
  }

  get filterRowsArray(): FormArray {
    return this.form.get('filterRows') as FormArray;
  }

  get includeChipsArray(): FormArray {
    return this.form.get('includeChips') as FormArray;
  }

  get excludeChipsArray(): FormArray {
    return this.form.get('excludeChips') as FormArray;
  }

  get geolocatedAreasArray(): FormArray {
    return this.form.get('geolocatedAreas') as FormArray;
  }

  /** What MilaMultiSelect(Hierarchy) binds to on 'flat'/'hierarchy' rows. */
  rowSelectControlOf(rowCtrl: AbstractControl): FormControl<MilaMultiSelectOption[]> {
    return (rowCtrl as FormGroup).get('chips') as FormControl<MilaMultiSelectOption[]>;
  }

  rowChipCount(rowCtrl: AbstractControl): number {
    return rowCtrl.value.chips?.length ?? 0;
  }

  // Deliberately doesn't clear chips on uncheck see buildCikisiFields().
  toggleRow(rowCtrl: AbstractControl): void {
    const expanded = (rowCtrl as FormGroup).get('expanded')!;
    expanded.setValue(!expanded.value);
  }

  onFilterRowHeaderClick(meta: FilterRowMeta, rowCtrl: AbstractControl): void {
    if (meta.id === 'p-kt' && this.form.value.knowledgeBase) return;
    // Phones pick in a bottom sheet instead of the inline picker; the row
    // turns on once something is picked.
    if (isPhoneViewport()) {
      openCikisiFilterSheet(this.modalService, meta, this.flatFetchers[meta.id], rowCtrl);
      return;
    }
    this.toggleRow(rowCtrl);
  }

  private get knowledgeTopicsRow(): FormGroup {
    const i = this.FILTER_ROW_META.findIndex(m => m.id === 'p-kt');
    return this.filterRowsArray.at(i) as FormGroup;
  }

  toggleKnowledgeBase(): void {
    const control = this.form.get('knowledgeBase')!;
    const next = !control.value;
    control.setValue(next);

    const topicsRow = this.knowledgeTopicsRow;
    const chips = topicsRow.get('chips')!;
    if (next) {
      topicsRow.get('expanded')!.setValue(false);
      chips.disable();
    } else {
      chips.enable();
    }
  }

  /** Shared Enter/comma-to-add, Backspace-to-pop-last handler for any chip FormArray. */
  private pushChipFromInput(event: KeyboardEvent, chips: FormArray): void {
    const input = event.target as HTMLInputElement;
    if ((event.key === 'Enter' || event.key === ',') && input.value.trim()) {
      event.preventDefault();
      const value = input.value.trim();
      const isDuplicate = (chips.value as string[]).some(v => v.toLowerCase() === value.toLowerCase());
      if (!isDuplicate) {
        chips.push(this.fb.control(value));
      }
      input.value = '';
    }
    if (event.key === 'Backspace' && !input.value && chips.length) {
      chips.removeAt(chips.length - 1);
    }
  }

  onDomainKey(event: KeyboardEvent, chips: FormArray): void {
    this.pushChipFromInput(event, chips);
  }

  removeDomainChip(chips: FormArray, i: number): void {
    chips.removeAt(i);
  }

  openMapAreaPicker(): void {
    import('../map-area-picker/map-area-picker').then(({ MapAreaPicker }) => {
      this.modalService.full(MapAreaPicker, {
        data: this.geolocatedAreasArray.value,
      }).closed$.subscribe(result => {
        if (!result) return;
        this.geolocatedAreasArray.clear();
        result.forEach(area => this.geolocatedAreasArray.push(this.fb.control(area)));
      });
    });
  }

  openPromptOptimizer(): void {
    if (!this.form.value.question?.trim()) return;
    this.modalService.medium(PromptOptimizer, {
      data: { question: this.form.value.question ?? '', endpoint: 'rag' },
    }).closed$.subscribe(result => {
      if (!result) return;
      this.form.patchValue({ question: result });
    });
  }

  // ── Pricing ──────────────────────────────────────────────────────────
  /** Real backend param names currently in use, for surcharge lookup
   *  against the live catalog (see utils/endpoint-pricing.ts). */
  private get activeParamNames(): string[] {
    const v = this.form.value;
    const active: string[] = [];
    if (v.knowledgeBase) active.push('knowledge_base');
    if (v.geolocatedAreas?.length) active.push('geolocated_areas');
    if (v.includeRelevanceScore) active.push('include_relevance_score');
    if (v.includeOriginalLanguageSnippet) active.push('include_original_language_snippet');
    if (v.includeArticleTitle) active.push('include_article_title');
    if (v.includeImageUrl) active.push('include_image_url');
    if (v.includeDomainName) active.push('include_domain_name');
    if (v.includeFavicon) active.push('include_favicon');
    this.FILTER_ROW_META.forEach((meta, i) => {
      const row = v.filterRows?.[i];
      if (row?.expanded && row.chips?.length) active.push(meta.pricingParam);
    });
    return active;
  }

  /** Live price estimate, recomputed from current form state on every
   *  change against the real catalog see utils/endpoint-pricing.ts. */
  get estimatedPrice(): PriceEstimate {
    return estimateEndpointPrice(this.endpointDef(), this.activeParamNames);
  }

  get requestSummary(): string {
    return (this.form.value.question ?? '').trim();
  }

  /** An example from the intro (utils/endpoint-intros.ts) into the question. */
  useExample(text: string): void {
    this.form.patchValue({ question: text });
  }

  get codeSnippet(): string {
    return buildCodeSnippets('rag', 'rag', this.buildPayload())[this.activeLanguage];
  }

  setLanguage(lang: CodeLanguage): void {
    this.activeLanguage = lang;
  }

  copySnippet(): void {
    navigator.clipboard?.writeText(this.codeSnippet).then(() => {
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
    });
  }

  // ── Calendars ────────────────────────────────────────────────────────
  calFrom: CalState = this.newCal();
  calTo: CalState = this.newCal();

  private newCal(): CalState {
    const now = new Date();
    return { open: false, year: now.getFullYear(), month: now.getMonth(), selected: null };
  }

  toggleCal(cal: CalState, event: MouseEvent, title = 'Date'): void {
    event.stopPropagation();
    // Phones pick the date in a bottom sheet instead of the dropdown.
    if (isPhoneViewport()) {
      this.closeAll();
      openDateSheet(this.modalService, title, cal);
      return;
    }
    const wasOpen = cal.open;
    this.closeAll();
    if (!wasOpen) {
      cal.open = true;
    }
  }

  // ── Question tips panel (floats over the form, like Boolean operators) ──
  tipsOpen = false;

  @ViewChild('helpRows') private helpRows!: TemplateRef<unknown>;

  toggleTips(event: MouseEvent): void {
    event.stopPropagation();
    // Phones get the same rows as a bottom sheet instead of a popover.
    if (isPhoneViewport()) {
      this.closeAll();
      openHelpSheet(this.modalService, { title: 'Tips for better results', content: this.helpRows, panelClass: 'question-tips' });
      return;
    }
    const wasOpen = this.tipsOpen;
    this.closeAll();
    this.tipsOpen = !wasOpen;
  }

  closeTips(): void {
    this.tipsOpen = false;
  }

  closeAll(): void {
    this.calFrom.open = false;
    this.calTo.open = false;
    this.tipsOpen = false;
  }

  /** Bound to (appClickOutside) on each calendar wrapper: closes just that
   *  calendar, not every dropdown on the page. ClickOutsideDirective fires
   *  on mousedown, before click. Wiring the wrappers to the blanket
   *  closeAll() means clicking a day inside a calendar would register as
   *  "outside" the other wrappers too: their listeners would fire on that
   *  same mousedown and tear the calendar's DOM out (via closeAll()) before
   *  the browser ever dispatches the click, so selectDay() would silently
   *  never run. Each wrapper closing only its own state avoids that. */
  closeCal(cal: CalState): void {
    cal.open = false;
  }

  prevMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 0) { cal.month = 11; cal.year--; } else cal.month--;
  }

  nextMonth(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    if (cal.month === 11) { cal.month = 0; cal.year++; } else cal.month++;
  }

  calDays(cal: CalState): (number | null)[] {
    const first = new Date(cal.year, cal.month, 1).getDay();
    const count = new Date(cal.year, cal.month + 1, 0).getDate();
    const cells: (number | null)[] = Array(first).fill(null);
    for (let d = 1; d <= count; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  }

  selectDay(cal: CalState, day: number | null, event: MouseEvent): void {
    event.stopPropagation();
    if (!day) return;
    cal.selected = new Date(cal.year, cal.month, day);
    cal.open = false;
  }

  /** The × inside a date field that has a value. */
  clearDate(cal: CalState, event: MouseEvent): void {
    event.stopPropagation();
    cal.selected = null;
    cal.open = false;
  }

  isSelected(cal: CalState, day: number | null): boolean {
    return !!(day && cal.selected &&
      cal.selected.getDate() === day &&
      cal.selected.getMonth() === cal.month &&
      cal.selected.getFullYear() === cal.year);
  }

  isToday(cal: CalState, day: number | null): boolean {
    if (!day) return false;
    const t = new Date();
    return t.getDate() === day && t.getMonth() === cal.month && t.getFullYear() === cal.year;
  }

  formatDate(cal: CalState): string {
    if (!cal.selected) return '';
    return cal.selected.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  private isoDate(d: Date | null): string | null {
    return d ? d.toISOString().slice(0, 10) : null;
  }

  // ── Run query ────────────────────────────────────────────────────────
  private buildPayload() {
    const v = this.form.value;

    return {
      request: {
        question: v.question,
        published_from: this.isoDate(this.calFrom.selected),
        published_to: this.isoDate(this.calTo.selected),
        ...buildCikisiFields(this.isCikisiUser, 'cikisi_content_only', v.cikisiOnly, v.knowledgeBase, this.FILTER_ROW_META, v.filterRows),
        search_image_descriptions: v.searchImageDescriptions,
        include_domains: v.includeChips,
        exclude_domains: v.excludeChips,
        geolocated_areas: (v.geolocatedAreas as GeolocatedArea[]).map(({ id, type, coordinates }) => ({ id, type, coordinates })),
        include_languages: (v.languageSearch as MilaMultiSelectOption[]).map(o => o.id),
      },
      response: {
        use_source_popularity: v.useSourcePopularity,
        include_relevance_score: v.includeRelevanceScore,
        include_original_language_snippet: v.includeOriginalLanguageSnippet,
        include_article_title: v.includeArticleTitle,
        include_image_url: v.includeImageUrl,
        include_domain_name: v.includeDomainName,
        include_favicon: v.includeFavicon,
        relevance_criteria_count: v.relevanceCriteriaCount,
        comprehensiveness: v.comprehensiveness,
      },
    };
  }

  runQuery(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.notificationService.error('Enter a question before running the query.');
      return;
    }

    // The Run button is disabled for this already (see playground.html) this is
    // just a backstop, since the backend can only 422 without a key to bill.
    if (!this.apiKey.keys().length) {
      this.notificationService.error('Create an API key before running this query.');
      return;
    }

    const payload = this.buildPayload();

    // The include_* flags this run sends, read off the payload so the
    // skeleton matches the request exactly.
    this.loadingFields = new Set(Object.entries(payload.response)
      .filter(([key, on]) => key.startsWith('include_') && on === true)
      .map(([key]) => key));
    this.isLoading = true;
    this.errorMessage = null;
    this.playgroundService.runRag(payload, this.apiKey.selectedId() ?? undefined)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.status = 'error';
          this.response = null;
          this.runInfo = null;
          // A failed query (body.success false) arrives as an Error carrying the
          // API's messages; a transport failure as an HttpErrorResponse.
          this.errorMessage = error instanceof HttpErrorResponse
            ? apiErrorMessage(error, 'The request failed. Please try again later.')
            : error?.message || 'The query did not succeed.';
          this.notificationService.error('An error occurred while running the query. Please try again later.');
          return throwError(error);
        }),
        tap(({ result, latencyMs, credits, run }) => {
          this.response = result;
          this.latencyMs = latencyMs;
          this.credits = credits;
          this.runInfo = run;
          this.status = 'ok';
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  /** Called from the shared Playground footer's Reset button (see
   *  playground.ts/.html) once the user confirms the confirmation
   *  prompt itself lives there, not here, since it's one shared footer
   *  for all seven playground pages. */
  resetForm(): void {
    this.form = this.buildForm();
    this.status = 'idle';
    this.response = null;
    this.latencyMs = null;
    this.credits = null;
    this.runInfo = null;
    this.errorMessage = null;
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
